import NetworkError from '/shared/errors/NetworkError';
import type { NormalisedError } from '/index';

const UNKNOWN_ERROR_STRING = 'An unknown error occurred';
const NETWORK_ERROR_STRING = 'Network error. Please check your internet connection and try again.';

type ErrorDetail = Partial<NormalisedError> & {
  param?: string | null;
  details?: ErrorDetail | ErrorDetail[] | null;
};

type ErrorResponseData =
  | string
  | {
      error?: NormalisableError;
    };

type NormaliseErrorFunction = (...args: never[]) => NormaliseErrorInput;

export type NormaliseErrorInput =
  | Error
  | object
  | string
  | number
  | boolean
  | bigint
  | symbol
  | NormaliseErrorFunction
  | null
  | undefined;

interface ErrorLike {
  code?: string;
  error?: NormalisableError;
  message?: string;
  element?: string;
  param?: string;
  context?: string;
  details?: ErrorDetail[];
  description?: string;
  tokens?: Record<string, string>;
  isTranslated?: boolean;
  response?: { data?: ErrorResponseData };
}

type NormalisableError = ErrorLike | Error | string | null | undefined;

function isNormaliseErrorInput<ErrorValue>(error: ErrorValue): error is ErrorValue & NormaliseErrorInput {
  return (
    error === null ||
    error === undefined ||
    typeof error === 'object' ||
    typeof error === 'string' ||
    typeof error === 'number' ||
    typeof error === 'boolean' ||
    typeof error === 'bigint' ||
    typeof error === 'symbol' ||
    typeof error === 'function'
  );
}

export function toNormaliseErrorInput<ErrorValue>(error: ErrorValue): NormaliseErrorInput {
  if (isNormaliseErrorInput(error)) {
    return error;
  }
  return String(error);
}

function isErrorLike(error: NormalisableError): error is ErrorLike {
  return typeof error === 'object' && error !== null;
}

function toNormalisableError(error: NormaliseErrorInput): NormalisableError {
  if (error === null || error === undefined || typeof error === 'string' || error instanceof Error) {
    return error;
  }
  if (typeof error === 'object') {
    return error as ErrorLike;
  }
  return String(error);
}

function getNestedError(error: ErrorLike): NormalisableError {
  const data = error.response?.data;
  if (typeof data === 'object' && data !== null) {
    return data.error;
  }
  return undefined;
}

function getMessage(error: NormalisableError): { message: string } {
  if (error instanceof TypeError) {
    return { message: UNKNOWN_ERROR_STRING };
  }
  if (error instanceof SyntaxError) {
    return { message: UNKNOWN_ERROR_STRING };
  }
  if (error instanceof NetworkError) {
    return { message: NETWORK_ERROR_STRING };
  }
  if (!error) {
    return { message: UNKNOWN_ERROR_STRING };
  }
  if (typeof error === 'string') {
    return { message: error };
  }
  if (!isErrorLike(error)) {
    return { message: UNKNOWN_ERROR_STRING };
  }
  if (error.code && error.code === 'ERRNETWORK') {
    return { message: NETWORK_ERROR_STRING };
  }
  if (error.error) {
    return getMessage(error.error);
  }
  const nestedError = getNestedError(error);
  if (nestedError) {
    return getMessage(nestedError);
  }
  if (typeof error.response?.data === 'string') {
    return { message: error.response.data };
  }
  if (error.message) {
    return { message: error.message };
  }
  return { message: UNKNOWN_ERROR_STRING };
}

function getCode(error: NormalisableError): { code: string | null } {
  if (error instanceof TypeError) {
    return { code: 'ERRUNKNOWNERROR' };
  }
  if (error instanceof SyntaxError) {
    return { code: 'ERRUNKNOWNERROR' };
  }
  if (error instanceof NetworkError) {
    return { code: 'ERRNETWORKERROR' };
  }
  if (!error || typeof error === 'string') {
    return { code: null };
  }
  if (!isErrorLike(error)) {
    return { code: null };
  }
  if (error.code && error.code === 'ERRNETWORK') {
    return { code: 'ERRNETWORKERROR' };
  }
  if (error.code && !getNestedError(error)) {
    return { code: error.code };
  }
  const nestedError = getNestedError(error);
  if (nestedError) {
    return getCode(nestedError);
  }
  return { code: null };
}

function getElement(error: NormalisableError): { element: string | null } {
  if (!error || typeof error === 'string') {
    return { element: null };
  }
  if (!isErrorLike(error)) {
    return { element: null };
  }
  if (error.element) {
    return { element: error.element };
  }
  if (error.param) {
    return { element: error.param };
  }
  const nestedError = getNestedError(error);
  if (nestedError) {
    return getElement(nestedError);
  }
  return { element: null };
}

function getContext(error: NormalisableError, type: string | null = null): { context: string | null } {
  if (!error || typeof error === 'string') {
    if (type && type.indexOf('_REJECTED') > -1) {
      return { context: type.replace('_REJECTED', '') };
    }
    return { context: null };
  }
  if (!isErrorLike(error)) {
    return { context: null };
  }
  if (error.context) {
    return { context: error.context };
  }
  const nestedError = getNestedError(error);
  if (nestedError) {
    return getContext(nestedError);
  }
  if (type && type.indexOf('_REJECTED') > -1) {
    return { context: type.replace('_REJECTED', '') };
  }
  return { context: null };
}

function getDetails(error: NormalisableError): { details: NormalisedError['details'] } {
  if (!error || typeof error === 'string') {
    return { details: null };
  }
  if (!isErrorLike(error)) {
    return { details: null };
  }
  if (error.details && error.details.length > 0) {
    return {
      details: error.details.map((detail) => ({
        ...detail,
        created: new Date(),
      })) as NormalisedError['details'],
    };
  }
  const nestedError = getNestedError(error);
  if (nestedError) {
    return getDetails(nestedError);
  }
  return { details: null };
}

function getDescription(error: NormalisableError): { description: string | null } {
  if (!error || typeof error === 'string') {
    return { description: null };
  }
  if (!isErrorLike(error)) {
    return { description: null };
  }
  if (error.description) {
    return { description: error.description };
  }
  const nestedError = getNestedError(error);
  if (nestedError) {
    return getDescription(nestedError);
  }
  return { description: null };
}

function getTokens(error: NormalisableError): { tokens: Record<string, string> | null } {
  if (!error || typeof error === 'string') {
    return { tokens: null };
  }
  if (!isErrorLike(error)) {
    return { tokens: null };
  }
  if (error.tokens) {
    return { tokens: error.tokens };
  }
  const nestedError = getNestedError(error);
  if (nestedError) {
    return getTokens(nestedError);
  }
  return { tokens: null };
}

function getIsTranslated(error: NormalisableError): { isTranslated: boolean } {
  if (!error || typeof error === 'string') {
    return { isTranslated: false };
  }
  if (!isErrorLike(error)) {
    return { isTranslated: false };
  }
  if (error.isTranslated) {
    return { isTranslated: error.isTranslated };
  }
  return { isTranslated: false };
}

export default function normaliseError(error: NormaliseErrorInput = null, type: string | null = null): NormalisedError {
  const normalisableError = toNormalisableError(error);
  const { code } = getCode(normalisableError);
  const { element } = getElement(normalisableError);
  const { context } = getContext(normalisableError, type);
  const { details } = getDetails(normalisableError);
  const { message } = getMessage(normalisableError);
  const { description } = getDescription(normalisableError);
  const { tokens } = getTokens(normalisableError);
  const { isTranslated } = getIsTranslated(normalisableError);

  return {
    message,
    element,
    context,
    details,
    description,
    created: new Date(),
    type,
    code,
    tokens,
    isTranslated,
  };
}
